import React, { useEffect, useState } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import { holidayInfo } from "../../services/holidayAPI";

const CalendarModal = ({
  isOpen,
  toggle,
  selectedMonth,
  generateMonthGrid,
  formatDateKey,
  holidayMap,
  setHolidayMap,
  weekdayLabels,
  fetchHolidayInfo,
  plantId,
}) => {
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [addedMap, setAddedMap] = useState({});
  const [editedMap, setEditedMap] = useState({});
  const [removedMap, setRemovedMap] = useState({});
  const [prevValue, setPrevValue] = useState({});
  const [editingDate, setEditingDate] = useState(null);
  const [spanClickKey, setSpanClickKey] = useState(null);

   useEffect(() => {
    if (isOpen) {
      setPrevValue(holidayMap);
    }
  }, [isOpen]);

  if (!selectedMonth) return null;
  const year = new Date().getFullYear();
  const y = selectedMonth.getFullYear();
  const m = selectedMonth.getMonth();
  const cells = generateMonthGrid(y, m);

  const handleUpdateHolidays = async () => {
    if (
      Object.keys(addedMap).length === 0 &&
      Object.keys(editedMap).length === 0 &&
      Object.keys(removedMap).length === 0
    ) {
      setEditingDate(null);

      toggle();
      return;
    }

    setIsUpdateLoading(true);
    try {
      const payload = {
        request_type: "update",
        plant_id: plantId,
        update_info: [
          ...Object.entries(addedMap || {})
            .filter(([key]) => !removedMap[key])
            .map(([key, info]) => ({
              action: "add",
              date: key.replace(/-/g, "/"),
              info,
            })),

          ...Object.entries(editedMap || {})
            .filter(([key]) => !removedMap[key])
            .map(([key, info]) => ({
              action: "add",
              date: key.replace(/-/g, "/"),
              info,
            })),

          ...Object.entries(removedMap || {}).map(([key]) => ({
            action: "remove",
            date: key.replace(/-/g, "/"),
          })),
        ],
      };

      const response = await holidayInfo(payload);

      setAddedMap({});
      setEditedMap({});
      setRemovedMap({});
      setEditingDate(null);
      sessionStorage.setItem("restoreCalendarPosition", "true");
      toggle();
      fetchHolidayInfo(year, plantId);
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      toggle={toggle}
      backdrop="none"
      keyboard={false}
      size="md"
      className="customCalendarModalContent"
    >
      <ModalBody>
        <div className="customCalendar-box" style={{ position: "relative" }}>
          <button className="customOk-btn">
            {isUpdateLoading ? (
              <Spinner size="sm" />
            ) : (
              <span onClick={handleUpdateHolidays}>
                <img src="./Check.png" alt="edit" />
              </span>
            )}
          </button>

          <div className="customCalendar-header">
            {y}年{m + 1}月
          </div>

          <div className="customWeekday-row">
            {weekdayLabels.map((w) => (
              <div key={w} style={{ textAlign: "center" }}>
                {w}
              </div>
            ))}
          </div>

          <div className="customMonth-grid">
            {cells.map((cell, idx) => {
              const key = cell.date ? formatDateKey(cell.date) : null;
              const info =
                key && holidayMap[key] ? holidayMap[key] : addedMap[key];

              const hasAddChange = key
                ? addedMap[key] !== undefined && !holidayMap[key]
                : false;

              const hasRemoveChange = key
                ? removedMap[key] === true && holidayMap[key] != null
                : false;
              const originalValue = prevValue[key] || "";
              const currentValue = info || "";
              const hasUndoChange =
                !hasRemoveChange && currentValue !== originalValue;

              const isEditing = cell.date != null && editingDate === key;

              const classes = ["customDay-cell"];
              if (cell.inMonth) classes.push("customDay-inmonth ");

              if (info) classes.push("customHoliday");

              return (
                <div
                  key={idx}
                  onClick={(e) => {
                    if (!cell.date) {
                      setSpanClickKey(null);
                      setEditingDate(null);
                      return;
                    }

                    const key = formatDateKey(cell.date);

                    const originalValue = prevValue[key] || "";

                    if (addedMap[key] || removedMap[key]) {
                      setEditingDate(key); // allow undo only
                      return;
                    }

                    if (spanClickKey && spanClickKey === key) {
                      return;
                    }

                    //  If editing & clicking difn date, clear edit state
                    if (spanClickKey && spanClickKey !== key) {
                      setEditingDate(null);
                      setSpanClickKey(null);
                      return;
                    }

                    const info = holidayMap[key];
                    if (info && info !== originalValue) {
                      return;
                    }

                    // save previous for undo
                    setPrevValue((prev) => ({
                      ...prev,
                      [key]: info || "",
                    }));

                    // to remove holiday
                    if (info) {
                      setHolidayMap((prev) => ({
                        ...prev,
                        [key]: "",
                      }));

                      setRemovedMap((prev) => ({
                        ...prev,
                        [key]: true,
                      }));

                      setEditingDate(key);
                      return;
                    }

                    // to add holiday
                    setEditingDate(key);
                    setAddedMap((prev) => ({
                      ...prev,
                      [key]: "休み",
                    }));

                    if (hasUndoChange) {
                      setEditingDate(key);
                    }
                  }}
                  className={classes.join(" ")}
                  style={{ position: "relative", padding: "6px" }}
                >
                  {cell.day !== null ? (
                    <div>{cell.day}</div>
                  ) : (
                    <div style={{ opacity: 0.1 }}>&nbsp;</div>
                  )}

                  {/* remove holiday if already exist */}
                  {((isEditing && prevValue[key]) || hasRemoveChange) && (
                    <img
                      src="./undo.png"
                      className="undoImg"
                      alt="Undo"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHolidayMap((prev) => ({
                          ...prev,
                          [key]: prevValue[key],
                        }));
                        setRemovedMap((prev) => {
                          const newObj = { ...prev };
                          delete newObj[key];
                          return newObj;
                        });
                        setEditingDate(null);
                        setSpanClickKey(null);
                      }}
                    />
                  )}

                  {/*  Adding new holiday if no holiday */}
                  {isEditing && !prevValue[key] && (
                    <>
                      <img
                        src="./undo.png"
                        className="undoImg"
                        alt="Undo"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingDate(null);

                          setHolidayMap((prev) => ({
                            ...prev,
                            [key]: "",
                          }));

                          setAddedMap((prev) => {
                            const newObj = { ...prev };
                            delete newObj[key];
                            return newObj;
                          });
                        }}
                      />

                      <input
                        className="inputCalendar"
                        type="text"
                        autoFocus
                        maxLength={8}
                        value={info}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const newValue = e.target.value;

                          setHolidayMap((prev) => ({
                            ...prev,
                            [key]: newValue,
                          }));

                          setAddedMap((prev) => {
                            const next = { ...prev };

                            if (newValue.trim() === "") {
                              delete next[key];
                            } else {
                              next[key] = newValue;
                            }

                            return next;
                          });
                        }}
                        onBlur={() => {
                          setEditingDate(null);
                        }}
                      />
                    </>
                  )}

                  {/*  display undo after adding holiday */}
                  {(hasAddChange || hasUndoChange) &&
                    !isEditing &&
                    currentValue.trim() !== "" && (
                      <>
                        <img
                          src="./undo.png"
                          className="undoImg"
                          alt="Undo"
                          onClick={(e) => {
                            e.stopPropagation();

                            setHolidayMap((prev) => ({
                              ...prev,
                              [key]: prevValue[key] || "",
                            }));

                            setAddedMap((prev) => {
                              const newObj = { ...prev };
                              delete newObj[key];
                              return newObj;
                            });
                          }}
                        />

                        <span className="customHoliday-label">{info}</span>
                      </>
                    )}

                  {/* if info from api, click span to open input */}
                  {!isEditing && !hasAddChange && info && (
                    <span
                      className="customHoliday-label"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSpanClickKey(key);
                        setEditingDate(key);

                        // preserve old value for undo
                        // setPrevValue((prev) => ({
                        //   ...prev,
                        //   [key]: holidayMap[key] || "",
                        // }));

                        // move current value to updatedMap so text input appears
                        // setEditedMap((prev) => ({
                        //   ...prev,
                        //   [key]: holidayMap[key] || "",
                        // }));
                      }}
                    >
                      {info}
                    </span>
                  )}

                  {/* open input after span click */}
                  {isEditing && prevValue[key] && spanClickKey === key && (
                    <>
                      <input
                        className="inputCalendar"
                        type="text"
                        autoFocus
                        maxLength={8}
                        value={info}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const newValue = e.target.value;

                          setHolidayMap((prev) => ({
                            ...prev,
                            [key]: newValue,
                          }));

                          setEditedMap((prev) => {
                            const next = { ...prev };
                            if (
                              newValue.trim() === "" ||
                              newValue === prevValue[key]
                            ) {
                              // Remove from editedMap if no change
                              delete next[key];
                            } else {
                              // Only track if different from original API value
                              next[key] = newValue;
                            }
                            return next;
                          });
                        }}

                        // onBlur={() => {
                        //   setEditingDate(null);
                        //   setSpanClickKey(null);
                        // }}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CalendarModal;
